import type { SourceAudio } from '@/app/types';
import { materialsGetVoice, materialsListVoiceTasks } from '../apis';
import { VOICE_CLONE_TASKS_PAGE_SIZE } from '../constants';
import type { VoiceCloneJobCardStatus, VoiceCloneJobItem } from '../types';

type ListVoiceTasksResult = Awaited<ReturnType<typeof materialsListVoiceTasks>>;
type VoiceTask = NonNullable<NonNullable<ListVoiceTasksResult['data']>['tasks']>[number];

function placeholderAudio(voiceId: string, name?: string): SourceAudio {
  return { id: voiceId, name: name ?? '', src: '' };
}

function taskStatusToCardStatus(status?: VoiceTask['status']): VoiceCloneJobCardStatus {
  if (status === 'success') return 'succeeded';
  if (status === 'failed') return 'failed';
  if (status === 'created') return 'queued';
  return 'processing';
}

/**
 * 拉取当前用户最近一页音色复刻任务并映射为任务卡片数据（与 `materialsListVoiceTasks` 对齐）。
 */
export async function fetchVoiceCloneJobsFromApi(): Promise<VoiceCloneJobItem[]> {
  const { data } = await materialsListVoiceTasks({
    query: {
      'pager.pagesz': VOICE_CLONE_TASKS_PAGE_SIZE,
      'pager.page': 0,
      statuses: ['created', 'processing'],
    },
  });

  const tasks = data?.tasks ?? [];
  const jobs: VoiceCloneJobItem[] = [];

  for (const task of tasks) {
    const voiceId = task.voice_id;
    if (!voiceId) continue;

    if (task.status === 'success') {
      try {
        const voiceRes = await materialsGetVoice({ path: { voice_id: voiceId } });
        const voice = voiceRes.data;
        if (!voice) continue;
        jobs.push({
          voiceId,
          cardStatus: 'succeeded',
          audio: {
            id: voiceId,
            name: voice.name ?? task.name ?? '',
            src: voice.sample_uri ?? '',
          },
          voiceName: voice.name ?? task.name ?? '',
          voiceDescription: voice.description ?? '',
          jobSource: 'list',
          listTaskStatus: 'success',
        });
      } catch {
        jobs.push({
          voiceId,
          cardStatus: 'failed',
          audio: placeholderAudio(voiceId, task.name),
          voiceName: task.name ?? '',
          voiceDescription: '',
          jobSource: 'list',
          listTaskStatus: 'failed',
        });
      }
      continue;
    }

    jobs.push({
      voiceId,
      cardStatus: taskStatusToCardStatus(task.status),
      audio: placeholderAudio(voiceId, task.name),
      voiceName: task.name ?? '',
      voiceDescription: '',
      jobSource: 'list',
      listTaskStatus: task.status,
    });
  }

  return jobs;
}

export function mergeServerVoiceCloneJobs(prev: VoiceCloneJobItem[], server: VoiceCloneJobItem[]): VoiceCloneJobItem[] {
  const serverIds = new Set(server.map((j) => j.voiceId));
  const localOnly = prev.filter((j) => !serverIds.has(j.voiceId));
  return [...localOnly, ...server];
}
